// ==================== SUBSCRIPTION USAGE COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  MessageSquare, 
  Send,
  Crown,
  Clock,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { DashboardOverviewStats } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface SubscriptionUsageProps {
  stats: DashboardOverviewStats | null;
  isLoading?: boolean;
}

// ==================== PROGRESS BAR ====================

const UsageBar = ({ 
  label, 
  used, 
  limit, 
  icon: Icon,
  warningThreshold = 80,
}: { 
  label: string;
  used: number;
  limit: number;
  icon: typeof Users;
  warningThreshold?: number;
}) => {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isWarning = percentage >= warningThreshold;
  const isMaxed = percentage >= 100;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={12} className="text-(--color-muted-gray)" />
          <span className="text-xs text-(--color-soft-gray)">{label}</span>
        </div>
        <span className={cn(
          'text-xs font-medium',
          isMaxed ? 'text-(--color-error)' : isWarning ? 'text-(--color-warning)' : 'text-white'
        )}>
          {used.toLocaleString()} / {limit > 0 ? limit.toLocaleString() : '∞'}
        </span>
      </div>
      <div className="h-1.5 bg-(--color-navy-light) rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            isMaxed ? 'bg-(--color-error)' : isWarning ? 'bg-(--color-warning)' : 'bg-(--color-signal-orange)'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// ==================== COMPONENT ====================

export const SubscriptionUsage = ({ stats, isLoading = false }: SubscriptionUsageProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Plan Usage</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-3 animate-pulse">
              <div className="h-6 bg-(--color-border-gray) rounded" />
              <div className="h-5 bg-(--color-border-gray) rounded" />
              <div className="h-5 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const subscription = stats?.subscription;

  if (!subscription || subscription.status === 'INACTIVE') {
    return (
      <motion.div variants={staggerItem}>
        <Card className="border-yellow-500/30">
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Plan Usage</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="text-center py-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-2">
                <Crown size={20} className="text-yellow-400" />
              </div>
              <p className="text-sm text-white font-medium mb-0.5">No Active Plan</p>
              <p className="text-xs text-(--color-muted-gray) mb-3">
                Subscribe to a plan to unlock all features
              </p>
              <Link to="/app/upgrade-plan">
                <Button variant="primary" size="sm">
                  View Plans
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Plan Usage</CardTitle>
            <Badge variant="orange" className="gap-1 text-xs">
              <Crown size={10} />
              {subscription.planName}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Usage Bars */}
            <UsageBar
              label="Contacts"
              used={subscription.contactsUsed}
              limit={subscription.contactLimit}
              icon={Users}
            />
            <UsageBar
              label="Messages (this month)"
              used={subscription.messagesUsed}
              limit={subscription.messageLimit}
              icon={MessageSquare}
            />
            <UsageBar
              label="Campaigns (this month)"
              used={subscription.campaignsUsed}
              limit={subscription.campaignLimit}
              icon={Send}
            />

            {/* Days Remaining */}
            {subscription.daysRemaining !== null && (
              <div className={cn(
                'flex items-center gap-2 p-2 rounded-lg border',
                subscription.daysRemaining <= 7 
                  ? 'bg-yellow-500/10 border-yellow-500/30' 
                  : 'bg-(--color-navy-light) border-(--color-border-gray)'
              )}>
                {subscription.daysRemaining <= 7 ? (
                  <AlertTriangle size={14} className="text-yellow-400" />
                ) : (
                  <Clock size={14} className="text-(--color-muted-gray)" />
                )}
                <div className="flex-1">
                  <p className="text-[10px] text-(--color-muted-gray)">Subscription Period</p>
                  <p className={cn(
                    'text-xs font-medium',
                    subscription.daysRemaining <= 7 ? 'text-yellow-400' : 'text-white'
                  )}>
                    {subscription.daysRemaining} days remaining
                  </p>
                </div>
              </div>
            )}

            {/* Upgrade CTA */}
            <Link to="/app/upgrade-plan" className="block">
              <Button variant="secondary" size="sm" className="w-full text-xs">
                <ArrowRight size={12} />
                Upgrade Plan
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SubscriptionUsage;
